import { Drawer, Button, Select, Input, InputNumber, DatePicker, Grid, Space } from "antd";
import {
  CalendarOutlined,
  TagsOutlined,
  FlagOutlined,
  UserOutlined,
  AimOutlined,
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  fetchCrmFieldsApi,
  fetchCrmFieldDistinctValuesApi,
  fetchLeadSourceApi,
  fetchLeadStatusApi,
  fetchLeadPurposeApi,
} from "../../../../apiservice/crm";
import { useSelector } from "react-redux";
import "../../crmSharedStyle.css";

const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

const FieldLabel = ({ icon, children }) => (
  <div className="crm-field-label">
    {icon}
    {children}
  </div>
);

const LeadFilterDrawer = ({
  open,
  onClose,
  onApply,
  onReset,
  isAdmin,
}) => {
  const screens = useBreakpoint();
  const { filterState } = useSelector((s) => s.leads);

  const [filters, setFilters] = useState(filterState || {});
  const [statusList, setStatusList] = useState([]);
  const [sourceList, setSourceList] = useState([]);
  const [purposeList, setPurposeList] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [textFieldOptions, setTextFieldOptions] = useState({}); // field_key -> distinct values already used on some lead

  const users = useSelector((s) => s.lookups.users || []);

  useEffect(() => {
    loadMasters();
  }, []);

  useEffect(() => {
    if (open) {
      setFilters(filterState || {});
    }
  }, [open, filterState]);

  const loadMasters = async () => {
    try {
      const [statusRes, sourceRes, purposeRes, fieldRes] =
        await Promise.all([
          fetchLeadStatusApi(),
          fetchLeadSourceApi(),
          fetchLeadPurposeApi(),
          fetchCrmFieldsApi({ module: "lead" }),
        ]);

      setStatusList(statusRes.data || []);
      setSourceList(sourceRes.data || []);
      setPurposeList(purposeRes.data || []);

      const filterableFields = (fieldRes.data || []).filter((f) => f.show_in_filter);
      setCustomFields(filterableFields);

      // Text fields are filtered by picking from the values actually used
      // on some lead rather than free typing (see renderCustomField) --
      // fetch those once up front, same as the other master lists above.
      const textFields = filterableFields.filter(
        (f) => (f.field_type || f.type) === "text"
      );

      if (textFields.length) {
        const results = await Promise.all(
          textFields.map((f) =>
            fetchCrmFieldDistinctValuesApi({ field_key: f.field_key, module: "lead" })
          )
        );

        const optionsByKey = {};
        textFields.forEach((f, i) => {
          optionsByKey[f.field_key] = results[i]?.data || [];
        });
        setTextFieldOptions(optionsByKey);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleDateChange = (dates) => {
    setFilters((prev) => ({
      ...prev,
      date_from: dates?.[0]
        ? dates[0].startOf("day").format("YYYY-MM-DD HH:mm:ss")
        : null,
      date_to: dates?.[1]
        ? dates[1].endOf("day").format("YYYY-MM-DD HH:mm:ss")
        : null,
    }));
  };

  const renderCustomField = (field) => {
    const fieldType = field.field_type || field.type;

    if (["dropdown", "select"].includes(fieldType)) {
      return (
        <Select
          key={field.field_key}
          placeholder={field.field_name}
          style={{ width: "100%", marginBottom: 16 }}
          allowClear
          value={filters?.[field.field_key] ?? undefined}
          onChange={(v) =>
            setFilters((s) => ({
              ...s,
              [field.field_key]: v,
            }))
          }
          options={(field.options || []).map((o) => ({
            label:
              typeof o === "string"
                ? o
                : o.label || o.value,
            value:
              typeof o === "string"
                ? o
                : o.value || o.label,
          }))}
        />
      );
    }

    if (fieldType === "date") {
      // Mirrors the built-in "Date range" filter above: a single exact-day
      // match is rarely useful for filtering, so this always offers a
      // range, sent as "<field_key>_from"/"<field_key>_to". Stored custom
      // date values are plain "YYYY-MM-DD" strings (no time component --
      // see LeadDrawer.jsx's DATE field), so the bounds are sent the same
      // way rather than as full timestamps.
      const fromKey = `${field.field_key}_from`;
      const toKey = `${field.field_key}_to`;

      return (
        <RangePicker
          key={field.field_key}
          placeholder={[field.field_name, field.field_name]}
          style={{ width: "100%", marginBottom: 16 }}
          value={
            filters?.[fromKey] && filters?.[toKey]
              ? [dayjs(filters[fromKey]), dayjs(filters[toKey])]
              : null
          }
          onChange={(dates) =>
            setFilters((s) => ({
              ...s,
              [fromKey]: dates?.[0] ? dates[0].format("YYYY-MM-DD") : null,
              [toKey]: dates?.[1] ? dates[1].format("YYYY-MM-DD") : null,
            }))
          }
        />
      );
    }

    if (fieldType === "multi_select") {
      return (
        <Select
          key={field.field_key}
          mode="multiple"
          placeholder={field.field_name}
          style={{ width: "100%", marginBottom: 16 }}
          value={filters?.[field.field_key] || []}
          allowClear
          onChange={(v) =>
            setFilters((s) => ({
              ...s,
              [field.field_key]: v,
            }))
          }
          options={(field.options || []).map((o) => ({
            label:
              typeof o === "string"
                ? o
                : o.label || o.value,
            value:
              typeof o === "string"
                ? o
                : o.value || o.label,
          }))}
        />
      );
    }

    if (fieldType === "number") {
      // Numbers are naturally ordered, so a min/max range is what people
      // actually want to filter by ("budget over 10000"), not a pick-list
      // of exact values -- unlike text, where the values used are more
      // like a small fixed set of labels. Sent as
      // "<field_key>_from"/"<field_key>_to", same pairing as the date
      // range above.
      const fromKey = `${field.field_key}_from`;
      const toKey = `${field.field_key}_to`;

      return (
        <Space.Compact key={field.field_key} style={{ width: "100%", marginBottom: 16 }}>
          <InputNumber
            placeholder={`Min ${field.field_name}`}
            style={{ width: "50%" }}
            value={filters?.[fromKey] ?? null}
            onChange={(v) => setFilters((s) => ({ ...s, [fromKey]: v }))}
          />
          <InputNumber
            placeholder={`Max ${field.field_name}`}
            style={{ width: "50%" }}
            value={filters?.[toKey] ?? null}
            onChange={(v) => setFilters((s) => ({ ...s, [toKey]: v }))}
          />
        </Space.Compact>
      );
    }

    if (fieldType === "text") {
      // Picking from the values actually used (fetched in loadMasters)
      // instead of free-typing -- exact selection instead of a substring
      // guess, same reasoning as dropdown/multi-select above. showSearch
      // keeps this usable even if a lot of distinct values have been
      // entered, by letting the user type to narrow the list rather than
      // scrolling it.
      const options = textFieldOptions[field.field_key] || [];

      return (
        <Select
          key={field.field_key}
          mode="multiple"
          showSearch
          placeholder={field.field_name}
          style={{ width: "100%", marginBottom: 16 }}
          allowClear
          value={filters?.[field.field_key] || []}
          onChange={(v) =>
            setFilters((s) => ({
              ...s,
              [field.field_key]: v,
            }))
          }
          options={options.map((o) => ({ label: o, value: o }))}
          notFoundContent={options.length ? undefined : "No values entered yet"}
        />
      );
    }

    return (
      <Input
        key={field.field_key}
        placeholder={field.field_name}
        style={{ marginBottom: 16 }}
        value={filters?.[field.field_key] || ""}
        onChange={(e) =>
          setFilters((s) => ({
            ...s,
            [field.field_key]: e.target.value,
          }))
        }
      />
    );
  };

  return (
    <Drawer
      title="Filter Leads"
      open={open}
      onClose={onClose}
      width={screens.md ? 360 : "100%"}
      className="crm-drawer"
      styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
    >
      <div className="crm-drawer-body" style={{ padding: 0 }}>
        <div className="crm-form-section">
          <FieldLabel icon={<CalendarOutlined />}>Date range</FieldLabel>
          <RangePicker
            style={{ width: "100%" }}
            placeholder={["From Date", "To Date"]}
            value={
              filters?.date_from && filters?.date_to
                ? [dayjs(filters.date_from), dayjs(filters.date_to)]
                : null
            }
            onChange={handleDateChange}
          />
        </div>

        <div className="crm-form-section">
          <FieldLabel icon={<AimOutlined />}>Source</FieldLabel>
          <Select
            size="large"
            placeholder="Select Source"
            style={{ width: "100%" }}
            allowClear
            value={filters?.source ?? undefined}
            onChange={(v) => setFilters((s) => ({ ...s, source: v }))}
            options={sourceList.map((s) => ({ label: s.name, value: s.id }))}
          />
        </div>

        <div className="crm-form-section">
          <FieldLabel icon={<TagsOutlined />}>Status</FieldLabel>
          <Select
            placeholder="Status"
            size="large"
            style={{ width: "100%" }}
            allowClear
            value={filters?.status ?? undefined}
            onChange={(v) => setFilters((s) => ({ ...s, status: v }))}
            options={statusList.map((s) => ({ label: s.name, value: s.id }))}
          />
        </div>

        {isAdmin && (
          <div className="crm-form-section">
            <FieldLabel icon={<UserOutlined />}>Agent</FieldLabel>
            <Select
              placeholder="Agent"
              size="large"
              style={{ width: "100%" }}
              allowClear
              value={filters?.assigned_to ?? undefined}
              onChange={(v) => setFilters((s) => ({ ...s, assigned_to: v }))}
              options={users.map((u) => ({ label: u.full_name, value: u.id }))}
            />
          </div>
        )}

        <div className="crm-form-section">
          <FieldLabel icon={<FlagOutlined />}>Purpose</FieldLabel>
          <Select
            placeholder="Purpose"
            size="large"
            style={{ width: "100%" }}
            allowClear
            value={filters?.purpose ?? undefined}
            onChange={(v) => setFilters((s) => ({ ...s, purpose: v }))}
            options={purposeList.map((p) => ({ label: p.name, value: p.id }))}
          />
        </div>

        {customFields.length > 0 && (
          <div className="crm-form-section">
            <div className="crm-section-title">Custom fields</div>
            {customFields.map(renderCustomField)}
          </div>
        )}

        <div className="crm-drawer-footer">
          <Button
            size="large"
            onClick={() => {
              setFilters({
                search: "",
                source: null,
                status: null,
                purpose: null,
                assigned_to: null,
                date_from: null,
                date_to: null,
                sort_by: "created_desc",
              });

              onReset();
            }}
          >
            Reset
          </Button>

          <Button type="primary" size="large" onClick={() => onApply(filters)}>
            Apply Filters
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default LeadFilterDrawer;